/*
 * audio-decode-fallback.js — window.__decodeAudioFallback(file, opts)
 *
 * Last-resort decoder for audio codecs the BROWSER can't decode (Web Audio
 * decodeAudioData + WebCodecs both fail — e.g. Dolby E-AC-3 / AC-3, the "config
 * unsupported: ec-3" dead-end) but our shipped ffmpeg-WASM CAN (verified
 * 2026-07-11: eac3 → pcm, 6ch/48k, in-browser). Routes the source through
 * FFmpegProcessor → PCM WAV → an AudioBuffer, entirely client-side, no server.
 *
 * Reusable primitive (86 tool blades decode via Web Audio directly). Wired into
 * channel-converter first (proven demand); any other tool adopts it by calling
 * this AFTER its own decode tiers fail. Gated by a cheap byte-sniff so the 32 MB
 * ffmpeg core is fetched ONLY for actual Dolby content, never on a random
 * corrupt-file failure.
 *
 * Returns Promise<AudioBuffer|null> — null = "not my case / couldn't", so the
 * caller falls through to its normal decode-failed error.
 */
(function () {
    'use strict';

    // Sniff the first 256 KB for Dolby (E-)AC-3: MP4 sample-entry fourcc 'ec-3'/
    // 'ac-3' or config boxes 'dec3'/'dac3'; or the raw AC-3/E-AC-3 syncword 0x0B77.
    function sniffDolby(bytes) {
        if (bytes.length > 1 && bytes[0] === 0x0B && bytes[1] === 0x77) return true;
        var n = Math.min(bytes.length, 262144);
        for (var i = 0; i + 4 <= n; i++) {
            var a = bytes[i], b = bytes[i + 1], c = bytes[i + 2], d = bytes[i + 3];
            if ((a === 0x65 && b === 0x63 && c === 0x2D && d === 0x33) || // 'ec-3'
                (a === 0x61 && b === 0x63 && c === 0x2D && d === 0x33) || // 'ac-3'
                (a === 0x64 && b === 0x65 && c === 0x63 && d === 0x33) || // 'dec3'
                (a === 0x64 && b === 0x61 && c === 0x63 && d === 0x33))   // 'dac3'
                return true;
        }
        return false;
    }

    // Sniff DSD (1-bit Direct Stream Digital): DSF starts 'DSD ', DSDIFF starts
    // 'FRM8'. Browsers can't decode DSD at all — but our ffmpeg-WASM has the
    // dsd_lsbf/msbf decoders (verified: dsd_lsbf_planar → pcm_s24le). DSD can only
    // be worked with as PCM (1-bit → any channel math needs samples), so this is
    // the honest "accept DSD, decode to high-res PCM" path — NOT lossy magic.
    function sniffDsd(bytes) {
        if (bytes.length < 4) return false;
        var a = bytes[0], b = bytes[1], c = bytes[2], d = bytes[3];
        return (a === 0x44 && b === 0x53 && c === 0x44 && d === 0x20) || // 'DSD ' (DSF)
               (a === 0x46 && b === 0x52 && c === 0x4D && d === 0x38);    // 'FRM8' (DSDIFF/.dff)
    }

    // Parse a PCM WAV into an AudioBuffer with the EXACT channel count. We do NOT
    // route the WAV back through decodeAudioData because Safari/WebKit downmixes a
    // surround WAV to stereo there — which would silently defeat a 5.1 conversion.
    function wavToAudioBuffer(u8) {
        var dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
        if (dv.getUint32(0, false) !== 0x52494646) return null;       // 'RIFF'
        var off = 12, fmt = null, dataOff = 0, dataLen = 0;
        while (off + 8 <= u8.length) {
            var id = dv.getUint32(off, false), sz = dv.getUint32(off + 4, true);
            if (id === 0x666d7420) {                                  // 'fmt '
                fmt = { audioFmt: dv.getUint16(off + 8, true), ch: dv.getUint16(off + 10, true),
                        rate: dv.getUint32(off + 12, true), bits: dv.getUint16(off + 22, true) };
            } else if (id === 0x64617461) { dataOff = off + 8; dataLen = sz; break; } // 'data'
            off += 8 + sz + (sz & 1);
        }
        if (!fmt || !dataOff || !fmt.ch || !fmt.rate) return null;
        var ch = fmt.ch, bits = fmt.bits, bytesPer = bits / 8, stride = ch * bytesPer;
        var frames = Math.floor(dataLen / stride);
        if (frames <= 0) return null;
        var ac = new (window.AudioContext || window.webkitAudioContext)();
        var ab = ac.createBuffer(ch, frames, fmt.rate);
        try { ac.close(); } catch (_) {}
        for (var cc = 0; cc < ch; cc++) {
            var out = ab.getChannelData(cc), p = dataOff + cc * bytesPer, f;
            if (bits === 16) { for (f = 0; f < frames; f++) { out[f] = dv.getInt16(p, true) / 32768; p += stride; } }
            else if (bits === 24) { for (f = 0; f < frames; f++) { var v = (u8[p] | (u8[p + 1] << 8) | (u8[p + 2] << 16)); if (v & 0x800000) v |= ~0xFFFFFF; out[f] = v / 8388608; p += stride; } }
            else if (bits === 32 && fmt.audioFmt === 3) { for (f = 0; f < frames; f++) { out[f] = dv.getFloat32(p, true); p += stride; } }
            else if (bits === 32) { for (f = 0; f < frames; f++) { out[f] = dv.getInt32(p, true) / 2147483648; p += stride; } }
            else { try { ac.close(); } catch (_) {} return null; }
        }
        return ab;
    }

    window.__decodeAudioFallback = async function (file, opts) {
        opts = opts || {};
        try {
            if (!file) return null;
            var head = new Uint8Array(await file.slice(0, 262144).arrayBuffer());
            var isDsd = sniffDsd(head) || /dsd|\.dsf|\.dff/i.test(String(opts.errMsg || '') + '|' + (file.name || ''));
            var errHit = /ec-3|ac-3|eac3|ac3|config unsupported|dts|truehd|mlp/i.test(String(opts.errMsg || ''));
            if (!sniffDolby(head) && !isDsd && !errHit) return null;   // not our case → let the caller's error stand
            if (!window.FFmpegProcessor) return null;
            var onP = (typeof opts.onProgress === 'function') ? opts.onProgress : null;
            if (onP) try { onP(0.03); } catch (_) {}
            await window.FFmpegProcessor.init(onP ? function (p) { try { onP(0.03 + p * 0.5); } catch (_) {} } : undefined);
            // -map 0:a:0 = first audio stream only (drop cover art / video); pcm_s24le
            // preserves up to 24-bit (transparent for lossy E-AC-3, keeps headroom for
            // any lossless codec). Native channel count + rate for Dolby (no -ac/-ar).
            //   DSD: decode to 88.2 kHz (bake-off 2026-07-15: the audible-band tone is
            //   rate-independent, but native DSD decode is 352.8 kHz = 8× the buffer =
            //   an OOM liability for the whole-buffer pipeline; 88.2 kHz keeps the full
            //   audible band + headroom, ¼ the memory, and ffmpeg's resample lowpass
            //   strips the ultrasonic DSD shaped-noise). DSD is a DSD-family rate.
            var args = isDsd
                ? ['-map', '0:a:0', '-ar', '88200', '-c:a', 'pcm_s24le']
                : ['-map', '0:a:0', '-c:a', 'pcm_s24le'];
            var res = await window.FFmpegProcessor.process(file, args, 'ecdec.wav');
            if (onP) try { onP(0.96); } catch (_) {}
            var wav = new Uint8Array(await res.blob.arrayBuffer());
            var ab = wavToAudioBuffer(wav);
            return (ab && ab.numberOfChannels) ? ab : null;
        } catch (_) { return null; }
    };

    // Cheap boolean: is this a Dolby (E-AC-3/AC-3) source? Reads only the first
    // 256 KB. Used for DISCOVERABILITY — a Dolby file that the browser downmixed to
    // stereo at decode looks like plain stereo, so a tool can nudge the user to the
    // keep-layout / surround path instead of silently offering only a 2-ch upmix.
    window.__sniffDolby = async function (file) {
        try { return sniffDolby(new Uint8Array(await file.slice(0, 262144).arrayBuffer())); }
        catch (_) { return false; }
    };
})();
